import { useEffect, useRef } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not(:disabled)",
  "input:not(:disabled)",
  "select:not(:disabled)",
  "textarea:not(:disabled)",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * Keeps Tab and Shift+Tab focus inside a dialog, wrapping at either end.
 *
 * @param {KeyboardEvent|import("react").KeyboardEvent} event
 * @param {HTMLElement|null} dialog
 * @returns {void}
 */
const trapTabFocus = (event, dialog) => {
  const focusable = Array.from(
    dialog?.querySelectorAll(FOCUSABLE_SELECTOR) || []
  );

  if (focusable.length === 0) {
    event.preventDefault();

    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const { activeElement } = document;

  if (!focusable.includes(activeElement)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  } else if (event.shiftKey && activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

/**
 * Accessible modal dialog rendered into `document.body` through a portal, so
 * ancestors that create a containing block for fixed elements (for example a
 * card with `backdrop-filter`) can never confine or clip the overlay. While
 * open it locks page scroll, focuses `initialFocusRef` (or the dialog), traps
 * Tab focus, and closes on Escape unless busy, including after a backdrop click. Focus returns to the previously
 * focused element when it closes.
 *
 * @param {{
 *   children: import("react").ReactNode,
 *   initialFocusRef?: import("react").RefObject<HTMLElement>,
 *   isBusy?: boolean,
 *   isOpen: boolean,
 *   labelledBy: string,
 *   onClose: () => void,
 * }} props
 * @returns {import("react").ReactPortal|null}
 */
const ModalShell = ({
  children,
  initialFocusRef,
  isBusy = false,
  isOpen,
  labelledBy,
  onClose,
}) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    (initialFocusRef?.current || dialogRef.current)?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [initialFocusRef, isOpen]);

  // Listen on the document, not the dialog, so Escape and the Tab trap still
  // work after a click on the backdrop moves focus to the page body.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isBusy) {
        event.preventDefault();
        event.stopPropagation();
        onClose();

        return;
      }

      if (event.key === "Tab") trapTabFocus(event, dialogRef.current);
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isBusy, isOpen, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/60 px-4 backdrop-blur-md"
          style={{ zIndex: 100000 }}
        >
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            aria-busy={isBusy}
            aria-labelledby={labelledBy}
            aria-modal="true"
            className="relative max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-white/10"
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            ref={dialogRef}
            role="dialog"
            tabIndex={-1}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ModalShell;
